import { useMemo, useState } from "react";
import { useResource, formatDateTime, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { InitialsAvatar } from "../components/InitialsAvatar";
import type { Employee, Participant, ServiceNote } from "../lib/types";

/**
 * Progress notes over the existing service_note table (migration 008) and its
 * /service-notes route — NOT a new table. service_note is already the shift-
 * linked, participant-scoped, append-only care record (kind defaults to
 * 'progress'; corrections are made with a further note via amends_id, never an
 * edit). This page is the feed + compose surface the handoff described, mapped
 * onto that record.
 *
 * The web ServiceNote type omits columns the row actually carries (created_at,
 * amends_id, shift_id) — the entity router returns SELECT *, so widen locally
 * rather than edit the shared type.
 */
type Note = ServiceNote & {
    created_at?: string;
    amends_id?: string | null;
    shift_id?: string | null;
    author_user_id?: string | null;
};

// Handoff note types → note_kind enum (exact):
//   shift_note → progress · clinical → clinical · behaviour → behaviour
const NOTE_TYPES: Array<{ value: string; label: string }> = [
    { value: "progress", label: "Shift note" },
    { value: "clinical", label: "Clinical" },
    { value: "behaviour", label: "Behaviour" },
];

const kindChip = (k: string) =>
    k === "clinical" ? "info" : k === "behaviour" ? "warn" : "mute";
const kindLabel = (k: string) =>
    k === "progress" ? "Shift note" : titleCase(k.replace(/_/g, " "));

interface Draft {
    participant_id: string;
    kind: string;
    subject: string;
    body: string;
    amends_id: string | null;
}
const emptyDraft: Draft = { participant_id: "", kind: "progress", subject: "", body: "", amends_id: null };

export const ProgressNotes = () => {
    const { user } = useAuth();
    const notes = useResource<Note[]>("/service-notes");
    const participants = useResource<Participant[]>("/participants");
    const employees = useResource<Employee[]>("/employees");

    const [participantFilter, setParticipantFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [search, setSearch] = useState("");
    const [dialog, setDialog] = useState<{ draft: Draft; amending?: Note } | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const participantName = useMemo(() => {
        const m = new Map((participants.data ?? []).map((p) => [p.id, p.full_name]));
        return (id: string) => m.get(id) ?? "—";
    }, [participants.data]);

    const employeeName = useMemo(() => {
        const m = new Map((employees.data ?? []).map((e) => [e.id, e.full_name]));
        return (id: string | null | undefined) => (id ? m.get(id) ?? "—" : "—");
    }, [employees.data]);

    const visible = (notes.data ?? []).filter((n) =>
        (!participantFilter || n.participant_id === participantFilter) &&
        (!typeFilter || n.kind === typeFilter) &&
        (!search || `${n.subject} ${n.body}`.toLowerCase().includes(search.toLowerCase()))
    );

    const openCompose = () => { setError(null); setDialog({ draft: { ...emptyDraft } }); };
    const openAmend = (n: Note) => {
        setError(null);
        setDialog({
            amending: n,
            draft: { participant_id: n.participant_id, kind: n.kind, subject: n.subject, body: "", amends_id: n.id },
        });
    };

    const save = async () => {
        if (!dialog) return;
        const d = dialog.draft;
        if (!d.participant_id || !d.subject.trim() || !d.body.trim()) {
            setError("Participant, subject and note text are required");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            // Append-only: an amendment is a NEW note pointing at the original via
            // amends_id — the original is never edited or deleted.
            await api.post("/service-notes", {
                participant_id: d.participant_id,
                kind: d.kind,
                subject: d.subject.trim(),
                body: d.body.trim(),
                amends_id: d.amends_id,
            });
            setDialog(null);
            notes.reload();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Could not save note");
        } finally {
            setSaving(false);
        }
    };

    const setDraft = (patch: Partial<Draft>) =>
        setDialog((prev) => (prev ? { ...prev, draft: { ...prev.draft, ...patch } } : prev));

    return (
        <div>
            <div className="toolbar" style={{ marginBottom: "0.8rem" }}>
                <input placeholder="Search notes…" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select value={participantFilter} onChange={(e) => setParticipantFilter(e.target.value)}>
                    <option value="">All participants</option>
                    {(participants.data ?? []).map((p) => (
                        <option key={p.id} value={p.id}>{p.full_name}</option>
                    ))}
                </select>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                    <option value="">All types</option>
                    {NOTE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <div className="spacer" />
                {user && <button className="btn" onClick={openCompose}>+ Add note</button>}
            </div>

            <div className="feed">
                {visible.map((n) => (
                    <div key={n.id} className="card">
                        <div className="card-body">
                            <div className="person-row">
                                <InitialsAvatar name={employeeName(n.employee_id)} />
                                <div className="person-main">
                                    <div className="name">{participantName(n.participant_id)}</div>
                                    <div className="sub">
                                        {employeeName(n.employee_id)} · {formatDateTime(n.created_at ?? n.note_date)}
                                        {n.amends_id ? " · amends an earlier note" : ""}
                                    </div>
                                </div>
                                <span className={`chip ${kindChip(n.kind)}`}>{kindLabel(n.kind)}</span>
                            </div>
                            {n.subject && <p><b>{n.subject}</b></p>}
                            <p>{n.body}</p>
                            {n.follow_up && <p className="sub">Follow-up: {n.follow_up}</p>}
                            {user && (
                                <div className="row-actions">
                                    <button className="icon-btn" onClick={() => openAmend(n)}>Amend</button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {!visible.length && <p className="empty">No notes match.</p>}
            </div>

            {dialog && (
                <Modal
                    title={dialog.amending ? "Amend note (writes a new record)" : "Add progress note"}
                    subtitle={dialog.amending
                        ? "A service note is a clinical record. The original stays; this correction is linked to it."
                        : undefined}
                    onClose={() => setDialog(null)}
                    footer={
                        <>
                            <button className="btn" onClick={() => setDialog(null)}>Cancel</button>
                            <button className="btn" disabled={saving} onClick={() => void save()}>
                                {saving ? "Saving…" : "Save note"}
                            </button>
                        </>
                    }
                >
                    {error && <div className="error-banner">{error}</div>}
                    <div className="field">
                        <label>Participant *</label>
                        <select
                            value={dialog.draft.participant_id}
                            disabled={!!dialog.amending}
                            onChange={(e) => setDraft({ participant_id: e.target.value })}
                        >
                            <option value="">Select participant…</option>
                            {(participants.data ?? []).map((p) => (
                                <option key={p.id} value={p.id}>{p.full_name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="field">
                        <label>Type</label>
                        <select value={dialog.draft.kind} onChange={(e) => setDraft({ kind: e.target.value })}>
                            {NOTE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                    <div className="field">
                        <label>Subject *</label>
                        <input value={dialog.draft.subject} onChange={(e) => setDraft({ subject: e.target.value })} />
                    </div>
                    <div className="field">
                        <label>Note *</label>
                        <textarea
                            rows={5}
                            value={dialog.draft.body}
                            onChange={(e) => setDraft({ body: e.target.value })}
                            placeholder="What happened, what was done, anything to hand over…"
                        />
                    </div>
                </Modal>
            )}
        </div>
    );
};
